import React, { useState, useEffect, useRef, useCallback } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  MessageCircle, Plus, Pencil, Trash2, Check, X,
  Eye, EyeOff, Wifi, WifiOff, Globe, Key, Save,
  Zap, Bell, ShieldCheck, Tag, Settings, RefreshCw,
  AlertTriangle, BadgeCheck,
  FileText, Hash, Megaphone, Package, Lock, Copy,
} from "lucide-react";

// ── Shared styles ─────────────────────────────────────────────────────────────
const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

// ── Constants ──────────────────────────────────────────────────────────────────
const TEMPLATE_CATEGORIES = [
  { value: "AUTHENTICATION", label: "Autenticación (OTP)",     color: "bg-blue-100 text-blue-700",    icon: ShieldCheck },
  { value: "UTILITY",        label: "Utilitaria (Pedidos)",    color: "bg-violet-100 text-violet-700", icon: Package },
  { value: "MARKETING",      label: "Marketing",               color: "bg-amber-100 text-amber-700",  icon: Megaphone },
];

const TEMPLATE_PURPOSES = [
  { value: "otp",             label: "Código OTP / Verificación",      category: "AUTHENTICATION" },
  { value: "new_order",       label: "Nuevo pedido — restaurante",     category: "UTILITY" },
  { value: "order_confirmed", label: "Pedido confirmado — cliente",    category: "UTILITY" },
  { value: "order_delivered", label: "Pedido entregado — cliente",     category: "UTILITY" },
  { value: "password_reset",  label: "Recuperación de contraseña",     category: "UTILITY" },
  { value: "marketing",       label: "Mensaje de marketing",           category: "MARKETING" },
  { value: "custom",          label: "Propósito personalizado",        category: "UTILITY" },
];

const NOTIFICATION_EVENTS = [
  { key: "new_order",       icon: Package,     label: "Nuevo pedido",            desc: "Notifica al restaurante cuando llega un pedido pendiente de aceptar", recipient: "Restaurante" },
  { key: "otp",             icon: ShieldCheck, label: "Código de verificación",  desc: "Envía el código OTP para verificar el teléfono del usuario",           recipient: "Usuario" },
  { key: "order_confirmed", icon: Check,       label: "Pedido confirmado",       desc: "Notifica al cliente que su pedido fue aceptado por el restaurante",    recipient: "Cliente" },
  { key: "order_delivered", icon: BadgeCheck,  label: "Pedido entregado",        desc: "Notifica al cliente que su pedido fue entregado",                      recipient: "Cliente" },
  { key: "password_reset",  icon: Lock,        label: "Recuperar contraseña",    desc: "Envía enlace para restablecer la contraseña",                          recipient: "Usuario" },
];

const STATUS_OPTIONS = [
  { value: "pendiente",   label: "Pendiente",   color: "bg-amber-100 text-amber-700" },
  { value: "en_atencion", label: "En atención", color: "bg-blue-100 text-blue-700" },
  { value: "resuelto",    label: "Resuelto",    color: "bg-green-100 text-green-700" },
];

const LANGUAGES = [
  { value: "es",    label: "Español" },
  { value: "es_CO", label: "Español (Colombia)" },
  { value: "es_MX", label: "Español (México)" },
  { value: "en",    label: "English" },
  { value: "en_US", label: "English (US)" },
  { value: "pt_BR", label: "Português (Brasil)" },
];

function uuid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── Section wrapper ────────────────────────────────────────────────────────────
function Section({ title, desc, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-3">
        <div className="w-9 h-9 bg-supernova-50 rounded-xl flex items-center justify-center shrink-0">
          <Icon size={18} className="text-supernova-600" />
        </div>
        <div>
          <p className="font-bold text-gray-800 text-sm">{title}</p>
          {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Toggle ─────────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none">
      <div
        onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition-colors relative ${value ? "bg-supernova-500" : "bg-gray-200"}`}
      >
        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
      </div>
      {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
    </label>
  );
}

// ── Category badge ─────────────────────────────────────────────────────────────
function CategoryBadge({ category }) {
  const cat = TEMPLATE_CATEGORIES.find(c => c.value === category);
  if (!cat) return null;
  const Icon = cat.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cat.color}`}>
      <Icon size={10} /> {cat.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Meta API credentials
// ─────────────────────────────────────────────────────────────────────────────
const WEBHOOK_URL = "https://api.qkiere.com/api/whatsapp/webhook";

function MetaCredentialsTab({ config, onSave, saving }) {
  const [form, setForm]           = useState({ phoneNumberId: "", businessAccountId: "", accessToken: "", appSecret: "", webhookVerifyToken: "", active: false });
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [testing,  setTesting]    = useState(false);
  const [testOk,   setTestOk]     = useState(null); // null | {phone, name} | false
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookResult,  setWebhookResult]  = useState(null); // null | { handshakeOk, handshakeError, messageOk, messageError }

  useEffect(() => {
    if (config) {
      setForm({
        phoneNumberId:      config.phoneNumberId      || "",
        businessAccountId:  config.businessAccountId  || "",
        accessToken:        config.accessToken         || "",
        appSecret:          config.appSecret           || "",
        webhookVerifyToken: config.webhookVerifyToken   || "",
        active:             config.active              ?? false,
      });
    }
  }, [config]);

  const handleTest = async () => {
    if (!form.phoneNumberId || !form.accessToken) {
      toast.warn("Ingresa Phone Number ID y Access Token antes de probar");
      return;
    }
    setTesting(true);
    setTestOk(null);
    try {
      const res = await API.post("/settings/whatsapp-meta/test", {
        phoneNumberId: form.phoneNumberId,
        accessToken:   form.accessToken,
      });
      setTestOk(res.data?.data ?? true);
      // Auto-activar y guardar al verificar exitosamente
      const activated = { ...form, active: true };
      setForm(activated);
      await onSave(activated);
      toast.success("¡Conexión exitosa! Meta API activada y guardada.");
    } catch (err) {
      setTestOk(false);
      toast.error(err.response?.data?.message || "Error al verificar credenciales");
    } finally { setTesting(false); }
  };

  const handleSave = () => onSave({ ...form });

  // Prueba el camino de ENTRADA completo (handshake + un mensaje sintético firmado) —
  // distinto de "Probar conexión" arriba, que solo prueba poder enviar. Requiere que
  // Verify Token y App Secret ya estén guardados (usa lo que hay en BD, no lo del form
  // sin guardar), así que avisamos si el form tiene cambios sin guardar.
  const handleTestWebhook = async () => {
    if (form.webhookVerifyToken !== (config?.webhookVerifyToken || "") || form.appSecret !== (config?.appSecret || "")) {
      toast.warn("Guarda las credenciales antes de probar el webhook — la prueba usa lo que ya está guardado");
      return;
    }
    setTestingWebhook(true);
    setWebhookResult(null);
    try {
      const res = await API.post("/settings/whatsapp-meta/test-webhook");
      setWebhookResult(res.data?.data ?? null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error probando el webhook");
    } finally { setTestingWebhook(false); }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(WEBHOOK_URL).then(() => toast.success("URL copiada"));
  };

  return (
    <div className="space-y-5">
      {/* Active toggle */}
      <div className="flex items-center justify-between bg-gray-50 rounded-2xl px-4 py-3">
        <div>
          <p className="text-sm font-bold text-gray-800">Meta WhatsApp Business API</p>
          <p className="text-xs text-gray-500 mt-0.5">Activa para usar la API oficial de Meta para envíos</p>
        </div>
        <Toggle value={form.active} onChange={v => setForm({ ...form, active: v })} />
      </div>

      {/* Credentials */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
            Phone Number ID *
          </label>
          <input
            className={inp}
            value={form.phoneNumberId}
            onChange={e => setForm({ ...form, phoneNumberId: e.target.value })}
            placeholder="123456789012345"
          />
          <p className="text-xs text-gray-400 mt-1">Meta Business Manager → WhatsApp → Phone Numbers</p>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
            WhatsApp Business Account ID *
          </label>
          <input
            className={inp}
            value={form.businessAccountId}
            onChange={e => setForm({ ...form, businessAccountId: e.target.value })}
            placeholder="987654321098765"
          />
          <p className="text-xs text-gray-400 mt-1">Meta Business Manager → Configuración de la cuenta</p>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
            Access Token (Token de acceso permanente) *
          </label>
          <div className="relative">
            <input
              className={`${inp} pr-10 font-mono text-xs`}
              type={showToken ? "text" : "password"}
              value={form.accessToken}
              onChange={e => setForm({ ...form, accessToken: e.target.value })}
              placeholder="EAAxxxxx... (token permanente de usuario del sistema)"
            />
            <button
              type="button"
              onClick={() => setShowToken(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Meta Business Manager → Usuarios del sistema → Generar token → Seleccionar permisos: whatsapp_business_messaging, whatsapp_business_management
          </p>
        </div>
      </div>

      {/* Webhook (mensajes entrantes) */}
      <div className="border-t border-gray-100 pt-5">
        <p className="text-sm font-bold text-gray-800 mb-1">Webhook de mensajes entrantes</p>
        <p className="text-xs text-gray-500 mb-3">Necesario para recibir mensajes en el panel de Conversaciones y que la IA pueda responder.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
              App Secret
            </label>
            <div className="relative">
              <input
                className={`${inp} pr-10 font-mono text-xs`}
                type={showSecret ? "text" : "password"}
                value={form.appSecret}
                onChange={e => setForm({ ...form, appSecret: e.target.value })}
                placeholder="App Secret de tu app de Meta"
              />
              <button
                type="button"
                onClick={() => setShowSecret(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Meta Developers → Tu app → Configuración básica</p>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">
              Verify Token
            </label>
            <input
              className={`${inp} font-mono text-xs`}
              value={form.webhookVerifyToken}
              onChange={e => setForm({ ...form, webhookVerifyToken: e.target.value })}
              placeholder="una-clave-secreta-que-tu-definas"
            />
            <p className="text-xs text-gray-400 mt-1">Inventa un valor y pégalo también en Meta al suscribir el webhook</p>
          </div>
        </div>
        <div className="mt-3 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
          <p className="text-xs font-bold text-gray-500 mb-1">URL del webhook (pégala en Meta Developers → WhatsApp → Configuration)</p>
          <div className="flex items-center gap-2">
            <code className="text-xs text-gray-700 break-all flex-1">{WEBHOOK_URL}</code>
            <button type="button" onClick={copyWebhookUrl} className="text-gray-400 hover:text-gray-700 shrink-0">
              <Copy size={13} />
            </button>
          </div>
        </div>

        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-xs text-amber-800">
          <p className="font-bold mb-1">En Meta Developers, además de pegar la URL y el Verify Token:</p>
          <p>Tienes que activar también el toggle de suscripción del campo <strong>"messages"</strong> — es un paso aparte de verificar la URL, y es el que más se pasa por alto. Sin eso, Meta nunca manda mensajes reales aunque la URL quede "verificada".</p>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={handleTestWebhook}
            disabled={testingWebhook}
            className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}
          >
            {testingWebhook ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
            Probar webhook
          </button>
          <p className="text-xs text-gray-400">Prueba de verdad que un mensaje entrante llegue y se guarde — no solo que la URL responda.</p>
        </div>

        {webhookResult && (
          <div className="mt-3 space-y-2">
            <div className={`flex items-start gap-2 rounded-xl px-3 py-2.5 border text-xs ${webhookResult.handshakeOk ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"}`}>
              {webhookResult.handshakeOk ? <Check size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
              <div>
                <p className="font-bold">{webhookResult.handshakeOk ? "Verificación de Meta (handshake): OK" : "Verificación de Meta (handshake): falló"}</p>
                {webhookResult.handshakeError && <p className="mt-0.5">{webhookResult.handshakeError}</p>}
              </div>
            </div>
            <div className={`flex items-start gap-2 rounded-xl px-3 py-2.5 border text-xs ${webhookResult.messageOk ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"}`}>
              {webhookResult.messageOk ? <Check size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
              <div>
                <p className="font-bold">{webhookResult.messageOk ? "Mensaje de prueba guardado: OK" : "Mensaje de prueba guardado: falló"}</p>
                {webhookResult.messageError && <p className="mt-0.5">{webhookResult.messageError}</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test result */}
      {testOk !== null && (
        <div className={`flex items-start gap-3 rounded-2xl px-4 py-3 border ${testOk ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
          {testOk
            ? <><Check size={16} className="text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-green-800">Conexión exitosa</p>
                  {typeof testOk === "object" && testOk.phone && (
                    <p className="text-xs text-green-700 mt-0.5">Número: {testOk.phone} · Nombre: {testOk.name}</p>
                  )}
                </div></>
            : <><AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm font-bold text-red-700">Credenciales inválidas o sin permisos</p></>
          }
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={handleTest}
          disabled={testing || !form.phoneNumberId || !form.accessToken}
          className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}
        >
          {testing ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
          Probar conexión
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}
        >
          <Save size={14} /> {saving ? "Guardando…" : "Guardar credenciales"}
        </button>
      </div>

      {/* Meta guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-xs text-blue-800 space-y-1.5">
        <p className="font-bold flex items-center gap-1.5 text-sm"><Globe size={14} /> Dónde obtener las credenciales</p>
        <ol className="list-decimal list-inside space-y-1 text-blue-700">
          <li>Accede a <strong>developers.facebook.com</strong> → Tu app → WhatsApp</li>
          <li>En <strong>API Setup</strong> encontrarás el Phone Number ID y la cuenta de prueba</li>
          <li>Para producción, agrega un número real en <strong>Phone Numbers</strong></li>
          <li>Crea un <strong>Usuario del sistema</strong> en Business Manager y genera un token permanente</li>
          <li>Asigna el activo de la app con permisos <code>whatsapp_business_messaging</code></li>
        </ol>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Library servers (existing functionality, refactored)
// ─────────────────────────────────────────────────────────────────────────────
const LIB_EMPTY = { name: "", url: "", token: "", active: true };

function LibraryTab({ delayMs, onSaveDelay }) {
  const [servers,   setServers]   = useState([]);
  const [modal,     setModal]     = useState(null); // null | 'create' | server
  const [form,      setForm]      = useState(LIB_EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [delay,     setDelay]     = useState(delayMs ?? 10000);

  useEffect(() => { loadServers(); }, []);
  useEffect(() => { setDelay(delayMs ?? 10000); }, [delayMs]);

  const loadServers = async () => {
    try {
      const res = await API.get("/whatsapp?type=library");
      setServers(res.data?.data?.servers ?? res.data?.data ?? []);
    } catch { toast.error("Error cargando servidores"); }
  };

  const openCreate = () => { setForm(LIB_EMPTY); setShowToken(false); setModal("create"); };
  const openEdit   = (s) => { setForm({ ...s }); setShowToken(false); setModal(s); };
  const closeModal = () => { setModal(null); setForm(LIB_EMPTY); };

  const handleSave = async () => {
    if (!form.name || !form.url || !form.token) { toast.warn("Nombre, URL y token son requeridos"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        await API.post("/whatsapp", { ...form, type: "library" });
        toast.success("Servidor creado");
      } else {
        await API.put(`/whatsapp/${modal.id}`, form);
        toast.success("Servidor actualizado");
      }
      await loadServers();
      closeModal();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (s) => {
    try {
      await API.put(`/whatsapp/${s.id}`, { active: !s.active });
      await loadServers();
    } catch { toast.error("Error al cambiar estado"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este servidor?")) return;
    try { await API.delete(`/whatsapp/${id}`); await loadServers(); toast.success("Eliminado"); }
    catch { toast.error("Error al eliminar"); }
  };

  const handleSaveDelay = async () => {
    await onSaveDelay(delay);
  };

  return (
    <div className="space-y-5">
      {/* Servers */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{servers.length} servidor{servers.length !== 1 ? "es" : ""} configurado{servers.length !== 1 ? "s" : ""}</p>
        <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
          <Plus size={14} /> Nuevo servidor
        </button>
      </div>

      {servers.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <Globe size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No hay servidores de biblioteca configurados</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {servers.map(s => (
            <div key={s.id} className={`border rounded-2xl p-4 shadow-sm ${s.active ? "border-green-200 bg-green-50/30" : "border-gray-200 opacity-70"}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.active ? "bg-green-100" : "bg-gray-100"}`}>
                    <MessageCircle size={15} className={s.active ? "text-green-600" : "text-gray-400"} />
                  </div>
                  <p className="font-bold text-gray-800 text-sm truncate">{s.name}</p>
                </div>
                <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${s.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {s.active ? "Activo" : "Inactivo"}
                </span>
              </div>
              <p className="text-xs font-mono text-gray-500 truncate bg-gray-50 rounded-lg px-2.5 py-1.5 mb-3">{s.url}</p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => handleToggle(s)} className={`${btn} text-xs border ${s.active ? "border-gray-200 text-gray-600 hover:bg-gray-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}>
                  {s.active ? <WifiOff size={11} /> : <Wifi size={11} />} {s.active ? "Desactivar" : "Activar"}
                </button>
                <button onClick={() => openEdit(s)} className={`${btn} text-xs bg-blue-50 text-blue-600 hover:bg-blue-100`}>
                  <Pencil size={11} /> Editar
                </button>
                <button onClick={() => handleDelete(s.id)} className={`${btn} text-xs bg-red-50 text-red-500 hover:bg-red-100 ml-auto`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delay config */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <p className="text-sm font-bold text-gray-700 mb-1">Demora entre mensajes masivos</p>
        <p className="text-xs text-gray-400 mb-3">Evita bloqueos al enviar mensajes en masa desde la biblioteca</p>
        <div className="flex items-center gap-4">
          <input
            type="range" min={0} max={60000} step={1000}
            value={delay}
            onChange={e => setDelay(Number(e.target.value))}
            className="flex-1 accent-supernova-500"
          />
          <span className="text-sm font-bold text-gray-700 w-16 text-right shrink-0">{delay / 1000}s</span>
          <button onClick={handleSaveDelay} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 shrink-0`}>
            <Save size={13} /> Guardar
          </button>
        </div>
      </div>

      {/* Modal */}
      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-800 mb-4">{modal === "create" ? "Nuevo servidor" : "Editar servidor"}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                <input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mi servidor WPP" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">URL del servidor *</label>
                <input className={inp} value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://mi-servidor.com" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Token de autenticación *</label>
                <div className="relative">
                  <input className={`${inp} pr-9`} type={showToken ? "text" : "password"} value={form.token} onChange={e => setForm({ ...form, token: e.target.value })} placeholder="Bearer token..." />
                  <button type="button" onClick={() => setShowToken(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <Toggle value={form.active} onChange={v => setForm({ ...form, active: v })} label="Activo" />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
                <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — Templates (Meta only)
// ─────────────────────────────────────────────────────────────────────────────
const TPL_EMPTY = {
  id: "", name: "", templateName: "", language: "es_CO",
  category: "UTILITY", purpose: "new_order",
  params: [{ key: "{{1}}", description: "" }],
  active: true,
};

function TemplatesTab({ templates, onSave }) {
  const [list,     setList]     = useState(templates ?? []);
  const [modal,    setModal]    = useState(null); // null | 'create' | index
  const [form,     setForm]     = useState({ ...TPL_EMPTY });

  useEffect(() => { setList(templates ?? []); }, [templates]);

  const openCreate = () => { setForm({ ...TPL_EMPTY, id: uuid() }); setModal("create"); };
  const openEdit   = (t, i) => { setForm({ ...t }); setModal(i); };
  const closeModal = () => { setModal(null); };

  const handleSave = () => {
    if (!form.name || !form.templateName) { toast.warn("Nombre y nombre en Meta son requeridos"); return; }
    let next;
    if (modal === "create") {
      next = [...list, form];
    } else {
      next = list.map((t, i) => i === modal ? form : t);
    }
    setList(next);
    onSave(next);
    closeModal();
  };

  const handleDelete = (i) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    const next = list.filter((_, idx) => idx !== i);
    setList(next);
    onSave(next);
  };

  const addParam = () => setForm(f => ({ ...f, params: [...f.params, { key: `{{${f.params.length + 1}}}`, description: "" }] }));
  const removeParam = (i) => setForm(f => ({ ...f, params: f.params.filter((_, idx) => idx !== i) }));

  const catFor = (purpose) => TEMPLATE_PURPOSES.find(p => p.value === purpose)?.category || "UTILITY";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{list.length} plantilla{list.length !== 1 ? "s" : ""} configurada{list.length !== 1 ? "s" : ""}</p>
        <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
          <Plus size={14} /> Nueva plantilla
        </button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileText size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Sin plantillas. Crea una para empezar.</p>
          <p className="text-xs mt-1 text-gray-300">Las plantillas deben estar aprobadas en Meta Business Manager</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((t, i) => {
            const purposeLabel = TEMPLATE_PURPOSES.find(p => p.value === t.purpose)?.label ?? t.purpose;
            return (
              <div key={t.id || i} className={`border rounded-2xl p-4 shadow-sm ${t.active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-800 text-sm truncate">{t.name}</p>
                    <code className="text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">{t.templateName}</code>
                  </div>
                  <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${t.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {t.active ? "Activa" : "Inactiva"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <CategoryBadge category={t.category || catFor(t.purpose)} />
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{purposeLabel}</span>
                  <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{t.language}</span>
                </div>
                {t.params?.length > 0 && (
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                    <Hash size={10} /> {t.params.length} parámetro{t.params.length !== 1 ? "s" : ""}:&nbsp;
                    {t.params.map(p => <code key={p.key} className="bg-gray-100 px-1 rounded">{p.key}</code>)}
                  </p>
                )}
                <div className="flex gap-2">
                  <button onClick={() => openEdit(t, i)} className={`${btn} text-xs bg-blue-50 text-blue-600 hover:bg-blue-100`}>
                    <Pencil size={11} /> Editar
                  </button>
                  <button onClick={() => handleDelete(i)} className={`${btn} text-xs bg-red-50 text-red-500 hover:bg-red-100 ml-auto`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">{modal === "create" ? "Nueva plantilla" : "Editar plantilla"}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre visible *</label>
                  <input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nuevo pedido restaurante" />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre en Meta *</label>
                  <input className={`${inp} font-mono text-xs`} value={form.templateName} onChange={e => setForm({ ...form, templateName: e.target.value.toLowerCase().replace(/\s+/g, "_") })} placeholder="nuevo_pedido" />
                  <p className="text-xs text-gray-400 mt-0.5">Exactamente como está registrada en Meta</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Categoría</label>
                  <select className={inp} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                    {TEMPLATE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Propósito</label>
                  <select className={inp} value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value, category: TEMPLATE_PURPOSES.find(p => p.value === e.target.value)?.category || form.category })}>
                    {TEMPLATE_PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Idioma</label>
                  <select className={inp} value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
                <div className="flex items-center">
                  <Toggle value={form.active} onChange={v => setForm({ ...form, active: v })} label="Plantilla activa" />
                </div>
              </div>

              {/* Parameters */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Parámetros del cuerpo</label>
                  <button onClick={addParam} className="text-xs text-supernova-600 hover:text-supernova-700 font-semibold flex items-center gap-1">
                    <Plus size={11} /> Agregar
                  </button>
                </div>
                <div className="space-y-2">
                  {form.params.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-2 rounded-lg font-mono shrink-0 w-14 text-center">{p.key}</code>
                      <input
                        className={`${inp} flex-1`}
                        value={p.description}
                        onChange={e => { const ps = [...form.params]; ps[i] = { ...p, description: e.target.value }; setForm({ ...form, params: ps }); }}
                        placeholder={`Descripción del parámetro ${i + 1} (ej: Nombre restaurante)`}
                      />
                      <button onClick={() => removeParam(i)} className="text-red-400 hover:text-red-600 shrink-0"><X size={14} /></button>
                    </div>
                  ))}
                  {form.params.length === 0 && <p className="text-xs text-gray-400 text-center py-2">Sin parámetros (plantilla fija)</p>}
                </div>
              </div>

              {/* Meta note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                <p className="font-bold mb-1">⚠️ Recuerda</p>
                <p>Las plantillas deben estar <strong>aprobadas en Meta Business Manager</strong> antes de usarlas. El nombre debe coincidir exactamente.</p>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSave} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
                <Save size={14} /> Guardar plantilla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 4 — Notifications / Events
// ─────────────────────────────────────────────────────────────────────────────
function NotificationsTab({ notifications, templates, onSave }) {
  const [notifs, setNotifs] = useState({});

  useEffect(() => { setNotifs(notifications ?? {}); }, [notifications]);

  const update = (key, patch) => {
    const next = { ...notifs, [key]: { ...(notifs[key] ?? {}), ...patch } };
    setNotifs(next);
    onSave(next);
  };

  const metaTemplates = (templates ?? []).filter(t => t.active);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500 mb-4">
        Configura qué canal y plantilla se usa para cada evento de la plataforma.
        Canal <strong>Meta</strong> = usa la API oficial de Meta con plantillas aprobadas.
        Canal <strong>Biblioteca</strong> = usa el servidor de Baileys/WPPConnect con texto libre.
      </p>

      {NOTIFICATION_EVENTS.map(ev => {
        const n = notifs[ev.key] ?? { enabled: false, channel: "library", templateId: null, message: "" };
        const Icon = ev.icon;
        return (
          <div key={ev.key} className={`border rounded-2xl p-4 transition ${n.enabled ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50/50 opacity-70"}`}>
            {/* Header row */}
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 bg-supernova-50 rounded-xl flex items-center justify-center shrink-0">
                <Icon size={16} className="text-supernova-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-gray-800">{ev.label}</p>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">→ {ev.recipient}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{ev.desc}</p>
              </div>
              <Toggle value={n.enabled} onChange={v => update(ev.key, { enabled: v })} />
            </div>

            {/* Config (only when enabled) */}
            {n.enabled && (
              <div className="pl-12 space-y-3">
                {/* Channel selector */}
                <div className="flex gap-2">
                  {[
                    { value: "meta",    label: "Meta API",  color: "border-blue-400 bg-blue-50 text-blue-700" },
                    { value: "library", label: "Biblioteca", color: "border-violet-400 bg-violet-50 text-violet-700" },
                  ].map(ch => (
                    <button
                      key={ch.value}
                      onClick={() => update(ev.key, { channel: ch.value })}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition ${n.channel === ch.value ? ch.color : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                    >
                      {ch.label}
                    </button>
                  ))}
                </div>

                {/* Meta: template selector */}
                {n.channel === "meta" && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">Plantilla de Meta</label>
                    {metaTemplates.length === 0 ? (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                        Sin plantillas activas. Ve a la pestaña <strong>Plantillas</strong> y crea una.
                      </p>
                    ) : (
                      <select
                        className={inp}
                        value={n.templateId ?? ""}
                        onChange={e => update(ev.key, { templateId: e.target.value || null })}
                      >
                        <option value="">— Selecciona una plantilla —</option>
                        {metaTemplates.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.templateName} · {t.language})
                          </option>
                        ))}
                      </select>
                    )}
                    {n.templateId && (() => {
                      const tpl = metaTemplates.find(t => t.id === n.templateId);
                      return tpl?.params?.length > 0 ? (
                        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs text-blue-800">
                          <p className="font-semibold mb-1">Parámetros de la plantilla:</p>
                          {tpl.params.map(p => (
                            <p key={p.key} className="flex items-center gap-1.5">
                              <code className="bg-blue-100 px-1 rounded">{p.key}</code> → {p.description}
                            </p>
                          ))}
                          <p className="mt-1.5 text-blue-600">El sistema asignará automáticamente los valores según el contexto del evento.</p>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Library: free text message */}
                {n.channel === "library" && ev.key !== "otp" && ev.key !== "password_reset" && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">
                      Mensaje personalizado
                      <span className="text-gray-400 font-normal ml-1">(variables: #{`{id}`}, ${`{total}`}, #{`{restaurant}`})</span>
                    </label>
                    <textarea
                      className={`${inp} resize-none min-h-[70px]`}
                      value={n.message ?? ""}
                      onChange={e => update(ev.key, { message: e.target.value })}
                      placeholder="Escribe el mensaje que recibirán los usuarios..."
                    />
                  </div>
                )}
                {n.channel === "library" && (ev.key === "otp" || ev.key === "password_reset") && (
                  <p className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                    El mensaje es generado automáticamente por el sistema para este evento.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main WhatsAppManager component
// ─────────────────────────────────────────────────────────────────────────────
// "Conversaciones" y "Agentes IA" viven ahora en su propio módulo — ver
// WhatsAppInbox.jsx (Panel admin → Multiagente WhatsApp) — para no duplicar la
// bandeja de chat en dos pantallas distintas que se desincronizarían entre sí.
const TABS = [
  { id: "meta",          label: "Meta API",      icon: Zap },
  { id: "library",       label: "Biblioteca",    icon: Globe },
  { id: "templates",     label: "Plantillas",    icon: FileText },
  { id: "notifications", label: "Notificaciones", icon: Bell },
];

export default function WhatsAppManager() {
  const [tab,     setTab]     = useState("meta");
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.get("/settings/whatsapp-meta");
      setConfig(res.data?.data?.config ?? {});
    } catch { toast.error("Error cargando configuración"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveFullConfig = async (patch) => {
    setSaving(true);
    try {
      const res = await API.put("/settings/whatsapp-meta", patch);
      setConfig(res.data?.data?.config ?? config);
      toast.success("Configuración guardada");
    } catch (err) { toast.error(err.response?.data?.message || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleSaveMeta          = (metaData) => saveFullConfig(metaData);
  const handleSaveTemplates     = (templates)     => saveFullConfig({ templates });
  const handleSaveNotifications = (notifications) => saveFullConfig({ notifications });

  const handleSaveDelay = async (delayMs) => {
    try {
      await API.put("/settings/whatsapp", { delayMs });
      toast.success("Demora guardada");
    } catch { toast.error("Error al guardar demora"); }
  };

  if (loading) return (
    <div className="space-y-3 animate-pulse">
      {[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-xl" />)}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-2xl flex items-center justify-center">
          <MessageCircle size={20} className="text-green-600" />
        </div>
        <div>
          <h2 className="text-lg font-black text-gray-800">WhatsApp</h2>
          <p className="text-sm text-gray-400">Configura la API de Meta y servidores de biblioteca</p>
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition flex-1 justify-center ${
              tab === id ? "bg-white shadow text-gray-800" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "meta" && (
        <Section title="Credenciales Meta WhatsApp Business" desc="Configura la API oficial de Meta para envío de mensajes con plantillas aprobadas" icon={Zap}>
          <MetaCredentialsTab config={config} onSave={handleSaveMeta} saving={saving} />
        </Section>
      )}

      {tab === "library" && (
        <Section title="Servidores de Biblioteca" desc="Baileys, WPPConnect u otros servidores propios. No requieren plantillas aprobadas." icon={Globe}>
          <LibraryTab delayMs={config?.delayMs} onSaveDelay={handleSaveDelay} />
        </Section>
      )}

      {tab === "templates" && (
        <Section title="Plantillas de Meta" desc="Configura las plantillas aprobadas en tu Meta Business Manager. Requeridas para el canal Meta API." icon={FileText}>
          <TemplatesTab templates={config?.templates ?? []} onSave={handleSaveTemplates} />
        </Section>
      )}

      {tab === "notifications" && (
        <Section title="Eventos y Notificaciones" desc="Define qué canal y mensaje se envía para cada evento de la plataforma" icon={Bell}>
          <NotificationsTab
            notifications={config?.notifications ?? {}}
            templates={config?.templates ?? []}
            onSave={handleSaveNotifications}
          />
        </Section>
      )}
    </div>
  );
}
